import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UsersService } from '../../service/entity-services/users.service';
import { AuthService } from '../../service/auth.service';
import { normalizeEmail } from '../../shared/utils/normalize-email';

@Component({
  selector: 'app-profile-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>person</mat-icon>
      Edit Profile
    </h2>

    <mat-dialog-content>
      @if (loading) {
        <div class="loading-container">
          <mat-spinner diameter="40"></mat-spinner>
          <p>{{ saving ? 'Saving...' : 'Loading profile...' }}</p>
        </div>
      } @else {
        <form [formGroup]="form">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Email</mat-label>
            <input matInput [value]="userEmail" disabled>
            <mat-icon matSuffix>email</mat-icon>
            <mat-hint>Email cannot be changed</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>First Name</mat-label>
            <input matInput formControlName="firstName" placeholder="Enter your first name">
            <mat-icon matSuffix>person</mat-icon>
            @if (form.get('firstName')?.hasError('required')) {
              <mat-error>First name is required</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Last Name</mat-label>
            <input matInput formControlName="lastName" placeholder="Enter your last name">
            <mat-icon matSuffix>person</mat-icon>
            @if (form.get('lastName')?.hasError('required')) {
              <mat-error>Last name is required</mat-error>
            }
          </mat-form-field>
        </form>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="onCancel()" [disabled]="saving">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
      <button mat-raised-button color="primary"
              (click)="onSave()"
              [disabled]="form.invalid || saving || loading">
        <mat-icon>save</mat-icon>
        Save
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2[mat-dialog-title] {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 16px 24px;
      border-bottom: 1px solid #e0e0e0;
    }

    mat-dialog-content {
      padding: 24px;
      min-width: 350px;
    }

    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      gap: 16px;
    }

    .loading-container p {
      margin: 0;
      color: rgba(0, 0, 0, 0.6);
    }

    mat-dialog-actions {
      padding: 16px 24px;
      border-top: 1px solid #e0e0e0;
    }

    mat-dialog-actions button {
      margin-left: 8px;
    }

    mat-dialog-actions button mat-icon {
      margin-right: 4px;
    }
  `]
})
export class ProfileDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<ProfileDialogComponent>);
  private fb = inject(FormBuilder);
  private usersService = inject(UsersService);
  private authService = inject(AuthService);

  form: FormGroup;
  userEmail = '';
  loading = true;
  saving = false;

  constructor() {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required]
    });
  }

  async ngOnInit() {
    try {
      // Get current user's email from auth service. NORMALIZE it (lowercase+trim):
      // the SSO token can return mixed case (e.g. Healthy_Contracts_1@cbhi.net), but
      // every backend read keys the record by the lowercased email. Saving the profile
      // under the raw-cased email would write to a different record than the one the
      // name-prompt reads — so the name would never "stick" and the prompt would fire
      // on every login. Normalizing here keeps the write key == the read key.
      this.userEmail = normalizeEmail(await this.authService.getUserEmail());
      console.log('ProfileDialog | Loading profile for:', this.userEmail);

      // Try to get existing profile data from the users list
      if (this.userEmail) {
        const users = await this.usersService.getAllUsersOnce();
        const currentUserData = users.find(u =>
          u.email?.toLowerCase() === this.userEmail.toLowerCase()
        );

        console.log('ProfileDialog | Found user in cache:', currentUserData);

        if (currentUserData) {
          const firstName = currentUserData.firstName || currentUserData.given_name || '';
          const lastName = currentUserData.lastName || currentUserData.family_name || '';
          console.log('ProfileDialog | Populating form with:', { firstName, lastName });
          this.form.patchValue({ firstName, lastName });
        } else {
          console.log('ProfileDialog | User not found in cache, form will be empty');
        }
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      this.loading = false;
    }
  }

  async onSave() {
    if (this.form.invalid || !this.userEmail) return;

    this.saving = true;
    this.loading = true;

    try {
      const firstName = this.form.value.firstName;
      const lastName = this.form.value.lastName;

      const profileData = {
        email: this.userEmail,
        firstName: firstName,
        lastName: lastName
      };

      console.log('ProfileDialog | Saving profile:', profileData);
      await this.usersService.updateUserProfile(profileData);

      // Immediately update the local cache so changes are reflected right away
      console.log('ProfileDialog | Updating local cache with:', { email: this.userEmail, firstName, lastName });
      this.usersService.updateUserInCache(this.userEmail, firstName, lastName);

      // NOTE: Not calling refreshAllUsers() here because:
      // 1. The users list comes from Cognito, not the profile DynamoDB table
      // 2. Refreshing would overwrite our local cache update with stale Cognito data
      // The local cache update above ensures immediate UI consistency

      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error saving profile:', error);
      this.saving = false;
      this.loading = false;
    }
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
